import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { QuickReplySetForm, QuickReplySetFormData, SendDatasetModal } from '../components/quick-replies';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const QuickReplySetEditScreen: React.FC<{ setId?: string }> = ({ setId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, screenParams } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState<Partial<QuickReplySetFormData> | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const formSubmitRef = useRef<(() => void) | null>(null);

  const actualSetId = setId || (screenParams as any)?.setId;

  useEffect(() => {
    if (actualSetId) {
      loadQuickReplySet();
    } else {
      setLoadingData(false);
    }
  }, [actualSetId]);

  const loadQuickReplySet = async () => {
    try {
      setLoadingData(true);
      const response = await apiService.getQuickReplySetById(actualSetId!);

      if (response.statusCode === 1000 && response.data) {
        setFormData({
          name: response.data.name,
          description: response.data.description,
          isActive: response.data.isActive,
          isDefault: response.data.isDefault,
        });
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load quick reply set';
        showNotification('error', 'Error', errorMessage);
        navigateTo('quick-replies');
      }
    } catch (error: any) {
      console.error('Error loading quick reply set:', error);
      showNotification('error', 'Error', error.message || 'Failed to load quick reply set');
      navigateTo('quick-replies');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (data: QuickReplySetFormData) => {
    try {
      setLoading(true);

      const response = await apiService.updateQuickReplySet(actualSetId!, {
        name: data.name,
        description: data.description,
        isActive: data.isActive,
        isDefault: data.isDefault,
      });

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', 'Success', 'Quick reply set updated successfully.');
        setTimeout(() => {
          navigateTo('quick-replies');
        }, 1500);
      } else {
        const errorMessage =
          response.errors?.[0]?.message || response.message || 'Failed to update quick reply set';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error updating quick reply set:', error);
      const errorMessage = error.message || 
                          error.errors?.[0]?.message || 
                          'Failed to update quick reply set';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-replies');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const handleBack = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-replies');
    }
  };

  if (loadingData) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading quick reply set...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
      formHeader={
        <FormHeader
          title="Edit Reply Set"
          subtitle="Update reply set details"
          onBack={handleBack}
          actions={[
            ...(actualSetId
              ? [
                  {
                    icon: 'send' as const,
                    onPress: () => setShowSendModal(true),
                    accessibilityLabel: 'Send',
                  },
                ]
              : []),
            {
              icon: 'checkmark' as const,
              primary: true,
              onPress: () => {
                if (formSubmitRef.current) {
                  formSubmitRef.current();
                }
              },
              loading,
              accessibilityLabel: 'Save Changes',
            },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {formData && (
          <QuickReplySetForm
            initialData={formData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Save Changes"
            loading={loading}
            hideActions={Platform.OS === 'web'}
            submitRef={formSubmitRef}
          />
        )}

        <SendDatasetModal
          visible={showSendModal}
          onClose={() => setShowSendModal(false)}
          onMessageSent={() => setShowSendModal(false)}
          defaultSetId={actualSetId}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});


